import { IconAdjustmentsHorizontal, IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { CustomizeLayoutModal } from "./CustomizeLayoutModal";
import {
  HREF_SOLICITUDES_CATALOGO,
  NAV_ADMIN_GRUPOS,
  NAV_PRINCIPAL,
  etiquetaNavItem,
  filtrarNavParaPermisos,
  type NavItem,
} from "@/components/layout/data/nav-items";
import { PERMISOS } from "@/lib/permisos";
import { isNavActive } from "@/components/layout/utils/path-matching";
import { useSolicitudesCatalogoPendientes } from "@/features/admin-solicitudes-catalogo/hooks/use-solicitudes-catalogo-admin";
import { cn } from "@/lib/cn";
import { Z_INDEX } from "@/lib/z-index";
import { Badge } from "../Badge";
import { Divider } from "../Divider";
import { Tooltip, TooltipContent, TooltipTrigger } from "../Tooltip";
import { SectionLabel } from "./SectionLabel";

/**
 * Sidebar del AppShell · sección "Navegación" (siempre) + sección
 * "Administración" (solo si el usuario ve al menos 1 item admin).
 *
 * `nav-items.ts`/`path-matching.ts` (`components/layout/`) son datos y
 * lógica pura SIN dependencia de Mantine — se reusan tal cual, sin fork.
 *
 * Dos modos: expandido (ícono + etiqueta) y `colapsado` (rail · solo
 * ícono centrado + tooltip a la derecha). El ANCHO real (72px↔248px) lo
 * fija el `AppShell` orquestador vía la CSS var `--shell-navbar-w`; este
 * componente solo decide qué renderiza en cada modo.
 */
export function AppShellNavbar({
  roles,
  permisos,
  pathname,
  mobileOpen,
  onItemClick,
  colapsado = false,
  onToggleColapso,
}: {
  /** Roles del usuario · solo para las ETIQUETAS dependientes de rol (no gatea). */
  roles: ReadonlyArray<string>;
  /** Permisos efectivos · gatean la visibilidad de los items (RBAC). */
  permisos: ReadonlyArray<string>;
  pathname: string;
  mobileOpen: boolean;
  onItemClick: () => void;
  colapsado?: boolean;
  onToggleColapso?: () => void;
}) {
  const navigate = useNavigate();
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const principal = filtrarNavParaPermisos(NAV_PRINCIPAL, permisos);
  // Cada grupo de administración con sus items visibles según permisos · se ocultan
  // los grupos vacíos (ej. un Auditor solo ve "Auditoría" → solo el grupo Sistema).
  const adminGrupos = NAV_ADMIN_GRUPOS.map((g) => ({
    titulo: g.titulo,
    items: filtrarNavParaPermisos(g.items, permisos),
  })).filter((g) => g.items.length > 0);
  const hayAdmin = adminGrupos.length > 0;

  const { data: pendientesCatalogo } = useSolicitudesCatalogoPendientes(
    permisos.includes(PERMISOS.adminSolicitudesCatalogo));

  function irA(href: string) {
    return () => {
      void navigate({ to: href as never });
      onItemClick();
    };
  }

  function renderItem(item: NavItem) {
    const activo = isNavActive(pathname, item.href);
    const label = etiquetaNavItem(item, roles);
    const conteo = item.href === HREF_SOLICITUDES_CATALOGO && (pendientesCatalogo ?? 0) > 0 ? pendientesCatalogo : undefined;
    const Icon = item.icon;

    const linkClass = cn(
      "flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-[13px] font-medium transition-colors",
      colapsado && "justify-center px-2",
      activo
        ? "bg-secondary font-semibold text-foreground shadow-2xs"
        : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground"
    );

    const link = (
      <button type="button" onClick={irA(item.href)} aria-label={label} className={cn(linkClass, "w-full")}>
        <Icon
          size={colapsado ? 18 : 16}
          stroke={1.7}
          className={cn("shrink-0", activo ? "text-multiva-600" : "text-muted-foreground")}
        />
        {!colapsado && <span className="flex-1 truncate text-left">{label}</span>}
        {!colapsado && conteo !== undefined && (
          <Badge size="xs" variant="filled" color="multiva" className="rounded-full">
            {conteo}
          </Badge>
        )}
      </button>
    );

    if (!colapsado) return <li key={item.href}>{link}</li>;
    return (
      <li key={item.href}>
        <Tooltip>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent side="right">{label}</TooltipContent>
        </Tooltip>
      </li>
    );
  }

  return (
    <nav
      className={cn(
        "multiva-navbar fixed left-0 flex flex-col overflow-visible border-r border-border bg-muted/40 transition-transform duration-200 ease-out-quint",
        // Sin prefijo de breakpoint, `w-62` quedaba activo TAMBIÉN en desktop y
        // empataba en especificidad con `md:w-[...]` (mismo peso, decide orden
        // de salida del CSS generado) · escopar ambas a su breakpoint con
        // `max-md:`/`md:` las vuelve mutuamente excluyentes, sin ambigüedad.
        "max-md:w-62 md:w-[var(--shell-navbar-w)]",
        mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
      )}
      style={{
        top: "var(--shell-header-h)",
        height: "calc(100dvh - var(--shell-header-h))",
        zIndex: Z_INDEX.appNavbar,
      }}
      aria-label="Navegación principal"
    >
      {/* Toggle flotante en el borde derecho (pill "<>") · solo desktop. */}
      {onToggleColapso && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onToggleColapso}
              aria-label={colapsado ? "Expandir menú" : "Colapsar menú"}
              className="absolute -right-3.5 top-2.5 z-10 hidden size-7 items-center justify-center rounded-md border border-border bg-background shadow-sm hover:bg-muted md:flex"
            >
              <span className="flex items-center leading-none">
                <IconChevronLeft size={12} strokeWidth={2.2} className="-mr-0.5" />
                <IconChevronRight size={12} strokeWidth={2.2} className="-ml-0.5" />
              </span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">{colapsado ? "Expandir menú" : "Colapsar menú"}</TooltipContent>
        </Tooltip>
      )}

      <div className="flex-1 overflow-y-auto px-2 py-3">
        {!colapsado && <SectionLabel>Navegación</SectionLabel>}
        <ul className="mb-4 flex flex-col gap-0.5">{principal.map(renderItem)}</ul>

        {hayAdmin && (
          <>
            {colapsado ? <Divider className="my-3" /> : <SectionLabel>Administración</SectionLabel>}
            {adminGrupos.map((grupo, i) => (
              <div key={grupo.titulo}>
                {colapsado ? (
                  i > 0 && <Divider className="my-2" />
                ) : (
                  <GrupoLabel className={i > 0 ? "mt-3" : undefined}>{grupo.titulo}</GrupoLabel>
                )}
                <ul className="flex flex-col gap-0.5">{grupo.items.map(renderItem)}</ul>
              </div>
            ))}
          </>
        )}
      </div>

      <div className="space-y-2.5 border-t border-border px-2 py-2.5">
        {/* Customize Layout · abre el modal de visibilidad de widgets del dashboard. */}
        <button
          type="button"
          onClick={() => setCustomizeOpen(true)}
          title="Customize Layout"
          className={cn(
            "flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-card py-2 text-xs font-semibold text-foreground shadow-2xs transition-colors hover:bg-muted",
            colapsado && "px-0"
          )}
        >
          <IconAdjustmentsHorizontal size={15} className="text-muted-foreground" />
          {!colapsado && <span>Customize Layout</span>}
        </button>

        {colapsado ? (
          <div className="flex justify-center">
            <Badge size="xs" variant="light" color="multiva">
              v2.0
            </Badge>
          </div>
        ) : (
          <div className="flex items-center justify-between px-1 text-[11px] font-medium text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-exito-500" aria-hidden="true" />
              Multiva
            </span>
            <span className="rounded bg-secondary px-1.5 py-0.5 font-mono text-[10px] text-secondary-foreground">v2.0</span>
          </div>
        )}
      </div>

      <CustomizeLayoutModal open={customizeOpen} onClose={() => setCustomizeOpen(false)} />
    </nav>
  );
}

/**
 * Sub-encabezado de una subcategoría del menú de administración · más discreto
 * que `SectionLabel` (normal-case y más tenue) para marcar la jerarquía
 * ADMINISTRACIÓN › grupo.
 */
function GrupoLabel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p className={cn("mb-1 px-2.5 text-[11px] font-medium text-muted-foreground/70", className)}>
      {children}
    </p>
  );
}
